/**
 * App principal
 * Sprint 6: Verificación de versión antes de renderizar la app
 */

import { useEffect } from 'react';
import { AppRouter } from './router';
import { useVersionStore } from './stores/versionStore';
import { UpdateRequiredScreen } from './pages/UpdateRequired';
import { Spinner, ToastViewport } from './components/ui';
import { useDismissKeyboardOnScroll } from './hooks/useDismissKeyboardOnScroll';

function App() {
  const { updateRequired, isLoading, checkVersion } = useVersionStore();

  useDismissKeyboardOnScroll();

  useEffect(() => {
    let rafId = 0;

    const setViewportMetrics = () => {
      const visualViewport = window.visualViewport;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const viewportWidth = visualViewport?.width ?? window.innerWidth;
      const rawViewportOffsetTop = Math.max(0, visualViewport?.offsetTop ?? 0);
      const viewportOffsetBottom = Math.max(
        0,
        window.innerHeight - viewportHeight - rawViewportOffsetTop
      );
      const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const isKeyboardVisible = isCoarsePointer && viewportOffsetBottom > 120;
      const viewportOffsetTop = isKeyboardVisible ? 0 : rawViewportOffsetTop;
      const clampedBottomInset = isKeyboardVisible ? 0 : Math.min(viewportOffsetBottom, 24);
      const compactViewport = viewportHeight < 700;
      const isLandscape = viewportWidth > viewportHeight;
      const fallbackTopInset = isCoarsePointer ? (isLandscape ? 8 : compactViewport ? 10 : 14) : 0;
      const fallbackBottomInset = isCoarsePointer ? (isLandscape ? 8 : compactViewport ? 12 : 16) : 0;

      document.documentElement.style.setProperty('--app-height', `${viewportHeight}px`);
      document.documentElement.style.setProperty('--app-offset-top', `${viewportOffsetTop}px`);
      document.documentElement.style.setProperty('--app-offset-bottom', `${clampedBottomInset}px`);
      document.documentElement.style.setProperty('--safe-fallback-top', `${fallbackTopInset}px`);
      document.documentElement.style.setProperty('--safe-fallback-bottom', `${fallbackBottomInset}px`);
    };

    const queueViewportMetricsUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(setViewportMetrics);
    };

    setViewportMetrics();
    window.addEventListener('resize', queueViewportMetricsUpdate);
    window.addEventListener('orientationchange', queueViewportMetricsUpdate);
    window.visualViewport?.addEventListener('resize', queueViewportMetricsUpdate);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', queueViewportMetricsUpdate);
      window.removeEventListener('orientationchange', queueViewportMetricsUpdate);
      window.visualViewport?.removeEventListener('resize', queueViewportMetricsUpdate);
    };
  }, []);

  useEffect(() => {
    checkVersion();
  }, [checkVersion]);

  // Splash mientras se verifica la versión
  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Spinner size="large" />
      </div>
    );
  }

  // Pantalla bloqueante si versión desactualizada
  if (updateRequired) {
    return <UpdateRequiredScreen />;
  }

  return (
    <>
      <AppRouter />
      <ToastViewport />
    </>
  );
}

export default App;
